<template>
  <q-layout view="hHh Lpr fFf" class="layout-root">
    <!-- Header with Breadcrumbs -->
    <template v-if="!isEmailPage">
    <q-header bordered class="bg-grey-9 text-white layout-header">
      <q-toolbar>
        <q-btn
          v-if="$q.screen.lt.md"
          flat
          round
          dense
          icon="menu"
          aria-label="Toggle navigation menu"
          class="q-mr-sm"
          @click="toggleDrawer"
        />
        
        <q-toolbar-title class="layout-title">
          <div class="title-section">
            <img src="/bar-tech-logo.svg" alt="Bar-Tech" class="logo" />
            <span class="app-name">The Portal</span>
          </div>
        </q-toolbar-title>

        <q-space />

        <!-- User Menu -->
        <q-btn-dropdown flat round icon="account_circle" aria-label="User account menu" class="user-menu">
          <q-list>
            <q-item v-close-popup clickable @click="goToProfile">
              <q-item-section avatar>
                <q-icon name="person" />
              </q-item-section>
              <q-item-section>Profile</q-item-section>
            </q-item>
            <q-item v-close-popup clickable @click="goToSettings">
              <q-item-section avatar>
                <q-icon name="settings" />
              </q-item-section>
              <q-item-section>Settings</q-item-section>
            </q-item>
            <q-separator />
            <q-item v-close-popup clickable @click="logout">
              <q-item-section avatar>
                <q-icon name="logout" />
              </q-item-section>
              <q-item-section>Logout</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </q-toolbar>
      
      <!-- Breadcrumbs -->
      <div class="breadcrumb-section">
        <q-breadcrumbs class="breadcrumbs">
          <q-breadcrumbs-el icon="home" to="/dashboard" />
          <q-breadcrumbs-el 
            v-for="crumb in breadcrumbs" 
            :key="crumb.path"
            :label="crumb.label" 
            :to="crumb.path" 
          />
        </q-breadcrumbs>
      </div>
    </q-header>
    </template>

    <!-- Navigation Drawer -->
    <template v-if="!isEmailPage">
    <q-drawer
      v-model="leftDrawerOpen"
      :persistent="$q.screen.lt.md"
      show-if-above
      bordered
      class="layout-drawer"
      :width="280"
    >
      <div class="drawer-layout">
        <!-- Navigation Menu -->
        <div class="nav-section">
          <q-scroll-area class="full-height">
            <q-list class="navigation-menu">
              <template v-for="item in navigationItems" :key="item.path || item.label">
                <!-- Group item with submenu -->
                <q-expansion-item
                  v-if="item.isGroup"
                  :icon="item.icon"
                  :label="item.label"
                  :caption="item.description"
                  expand-separator
                  class="nav-expansion"
                >
                  <q-list>
                    <template v-for="child in item.children" :key="child.path || child.label">
                      <!-- Nested group (sub-submenu) -->
                      <q-expansion-item
                        v-if="child.isGroup"
                        :icon="child.icon"
                        :label="child.label"
                        :caption="child.description"
                        expand-separator
                        class="nav-expansion nav-expansion--nested"
                      >
                        <q-list>
                          <q-item
                            v-for="subchild in child.children"
                            :key="subchild.path"
                            :to="subchild.path"
                            clickable
                            exact
                            class="nav-item nav-item--child nav-item--nested"
                            :class="{ 'nav-item--active': isActiveRoute(subchild.path) }"
                          >
                            <q-item-section avatar>
                              <q-icon :name="subchild.icon" />
                            </q-item-section>
                            <q-item-section>
                              <q-item-label>{{ subchild.label }}</q-item-label>
                              <q-item-label v-if="subchild.description" caption>
                                {{ subchild.description }}
                              </q-item-label>
                            </q-item-section>
                          </q-item>
                        </q-list>
                      </q-expansion-item>

                      <!-- Regular child item -->
                      <q-item
                        v-else
                        :to="child.path"
                        clickable
                        exact
                        class="nav-item nav-item--child"
                        :class="{ 'nav-item--active': isActiveRoute(child.path) }"
                      >
                        <q-item-section avatar>
                          <q-icon :name="child.icon" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ child.label }}</q-item-label>
                          <q-item-label v-if="child.description" caption>
                            {{ child.description }}
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                  </q-list>
                </q-expansion-item>

                <!-- Regular menu item (no submenu) -->
                <q-item
                  v-else
                  :to="item.path"
                  clickable
                  exact
                  class="nav-item"
                  :class="{ 'nav-item--active': isActiveRoute(item.path) }"
                >
                  <q-item-section avatar>
                    <q-icon :name="item.icon" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ item.label }}</q-item-label>
                    <q-item-label v-if="item.description" caption>
                      {{ item.description }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section v-if="item.badge" side>
                    <q-badge :color="item.badge.color">{{ item.badge.label }}</q-badge>
                  </q-item-section>
                </q-item>
              </template>
            </q-list>
          </q-scroll-area>
        </div>

        <!-- Quick Actions - Fixed at Bottom -->
        <div class="quick-actions">
          <q-separator class="q-my-md" />
          <q-item-label header>Quick Actions</q-item-label>
          <q-btn
            color="primary"
            icon="business_center"
            label="New Tender"
            class="full-width"
            @click="createTender"
          />
        </div>
      </div>
    </q-drawer>
    </template>

    <!-- Main Content -->
    <q-page-container class="layout-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-content">
          <q-spinner-dots size="50px" color="primary" />
          <div class="loading-text">{{ loadingMessage }}</div>
        </div>
      </div>

      <!-- Error Boundary -->
      <div v-else-if="hasError" class="error-container">
        <q-banner class="bg-red-1 text-red">
          <template #avatar>
            <q-icon name="error" color="red" />
          </template>
          <div>
            <div class="text-weight-bold">Something went wrong</div>
            <div>{{ errorMessage }}</div>
          </div>
          <template #action>
            <q-btn flat color="red" label="Retry" @click="retryOperation" />
            <q-btn flat color="red" label="Go Home" @click="goHome" />
          </template>
        </q-banner>
      </div>

      <!-- Main Router View -->
      <router-view v-else @loading="handleLoading" @error="handleError" />
    </q-page-container>

    <!-- Global Notifications -->
    <!-- Note: QDialog position prop only accepts: standard, top, right, bottom, left -->
    <q-dialog v-model="showAutoSaveNotification" position="top">
      <q-card class="auto-save-notification">
        <q-card-section>
          <div class="row items-center">
            <q-icon name="check_circle" color="positive" class="q-mr-sm" />
            <span>Auto-saved successfully</span>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-layout>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useQuasar } from 'quasar';
  import { useAuthStore } from './stores/auth';

  const $q = useQuasar();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();

  // Email page detection (hides header/drawer for standalone token-based pages)
  const isEmailPage = computed(() => route.meta.isEmailPage === true);

  // Layout state
  const leftDrawerOpen = ref(false);
  const isLoading = ref(false);
  const hasError = ref(false);
  const errorMessage = ref('');
  const loadingMessage = ref('Loading...');
  const showAutoSaveNotification = ref(false);

  // Navigation items with improved structure
  // Permission requirements:
  // - Dashboard: None (always visible)
  // - Customers: customers:1 (view)
  // - Sites: sites:1 (view)
  // - Tenders: quotes:1 (view)
  // - Quotes: quotes:1 (view)
  // - Projects: projects:1 (view)
  // - Timesheets: service:1 (view)
  // - Products: None (always visible)
  // - Templates: templates:1 (view)
  // - Settings: admin:1 (view)
  // - Users: admin:3 (edit)
  const allNavigationItems = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      description: 'Overview & insights',
      icon: 'dashboard',
      // No permission required - always visible
    },
    {
      path: '/organizations',
      label: 'Organizations',
      description: 'Organization management',
      icon: 'business',
      permission: { module: 'customers', level: 1 },
    },
    {
      path: '/sites',
      label: 'Sites',
      description: 'Site management',
      icon: 'location_city',
      permission: { module: 'sites', level: 1 },
    },
    {
      path: '/tenders',
      label: 'Tenders',
      description: 'Tender management',
      icon: 'business_center',
      permission: { module: 'quotes', level: 1 },
    },
    {
      path: '/projects',
      label: 'Projects',
      description: 'Project management',
      icon: 'engineering',
      permission: { module: 'projects', level: 1 },
    },
    {
      path: '/timesheets',
      label: 'Timesheets',
      description: 'Time tracking',
      icon: 'schedule',
      permission: { module: 'service', level: 1 },
    },
    {
      path: '/products',
      label: 'Products',
      description: 'Product catalog',
      icon: 'inventory_2',
      // No permission required - always visible
    },
    {
      path: '/admin/timesheets',
      label: 'Timesheet Admin',
      description: 'Review & approve timesheets',
      icon: 'fact_check',
      permission: { module: 'admin', level: 3 },
    },
    // Settings submenu group - last regular item before Help
    {
      path: '/settings',
      label: 'Settings',
      description: 'System configuration',
      icon: 'settings',
      isGroup: true,
      // Visible if user can access any settings section
      permissionCheck: (authStore) => {
        return authStore.hasPermission('quotes', 3) ||
          authStore.hasPermission('templates', 1) ||
          authStore.hasPermission('admin', 1);
      },
      children: [
        // Users & Access submenu
        {
          label: 'Users & Access',
          icon: 'admin_panel_settings',
          permission: { module: 'admin', level: 1 },
          isGroup: true,
          children: [
            {
              path: '/admin',
              label: 'User Management',
              icon: 'group',
              permission: { module: 'admin', level: 1 },
            },
            {
              path: '/admin/roles',
              label: 'Roles',
              icon: 'vpn_key',
              permission: { module: 'admin', level: 5 },
            },
            {
              path: '/admin/audit-logs',
              label: 'Audit Logs',
              icon: 'history',
              permission: { module: 'admin', level: 1 },
            },
          ],
        },
        // Tender Configuration submenu
        {
          label: 'Tender Configuration',
          icon: 'request_quote',
          permission: { module: 'quotes', level: 3 },
          isGroup: true,
          children: [
            {
              path: '/tender-config/inclusions',
              label: 'General Inclusions',
              icon: 'checklist',
              permission: { module: 'quotes', level: 3 },
            },
            {
              path: '/tender-config/qualifications',
              label: 'Scope Qualifications',
              icon: 'assignment',
              permission: { module: 'quotes', level: 3 },
            },
            {
              path: '/tender-config/branding',
              label: 'Branding',
              icon: 'palette',
              permission: { module: 'quotes', level: 3 },
            },
            {
              path: '/tender-config/supply-exclusions',
              label: 'Supply Exclusion Rules',
              icon: 'person_off',
              permission: { module: 'quotes', level: 3 },
            },
            {
              path: '/settings?panel=labour-categories',
              label: 'Labour Categories',
              icon: 'category',
              permission: { module: 'quotes', level: 3 },
            },
            {
              path: '/settings?panel=labour-templates',
              label: 'Labour Templates',
              icon: 'description',
              permission: { module: 'quotes', level: 3 },
            },
          ],
        },
        // Templates submenu
        {
          label: 'Templates',
          icon: 'library_books',
          permission: { module: 'templates', level: 1 },
          isGroup: true,
          children: [
            {
              path: '/templates/equipment',
              label: 'Equipment',
              icon: 'precision_manufacturing',
              permission: { module: 'templates', level: 1 },
            },
            {
              path: '/templates/devices',
              label: 'Devices',
              icon: 'sensors',
              permission: { module: 'templates', level: 1 },
            },
            {
              path: '/templates/compound',
              label: 'Compounds',
              icon: 'account_tree',
              permission: { module: 'templates', level: 1 },
            },
            {
              path: '/templates/controllers',
              label: 'Controllers',
              icon: 'memory',
              permission: { module: 'templates', level: 1 },
            },
          ],
        },
      ],
    },
    // Help at the very bottom
    {
      path: '/manual',
      label: 'Help',
      description: 'User manual & documentation',
      icon: 'help',
      // No permission required - always visible
    },
  ];

  // Filter navigation items based on user permissions (supports nested groups)
  const filterNavigationItem = (item) => {
    // Deep clone item to avoid mutating original
    const clonedItem = { ...item };

    // If item is a group, recursively filter children
    if (clonedItem.isGroup && clonedItem.children) {
      const visibleChildren = clonedItem.children
        .map((child) => filterNavigationItem(child))
        .filter(Boolean);

      // Only include group if it has visible children
      if (visibleChildren.length === 0) {return null;}

      // Replace children with filtered children
      clonedItem.children = visibleChildren;
    }

    // Check custom permission function
    if (clonedItem.permissionCheck) {
      if (!clonedItem.permissionCheck(authStore)) {
        return null;
      }
    }

    // Check standard permissions for the item
    if (clonedItem.permission) {
      const { module, level } = clonedItem.permission;
      if (!authStore.hasPermission(module, level)) {
        return null;
      }
    }

    // Legacy support: Check requiresAdmin flag (deprecated, use permission instead)
    if (clonedItem.requiresAdmin && !authStore.isAdmin) {
      return null;
    }

    // Legacy support: Check requiresManager flag (deprecated, use permission instead)
    if (clonedItem.requiresManager && !authStore.isManager) {
      return null;
    }

    return clonedItem;
  };

  const navigationItems = computed(() => {
    return allNavigationItems
      .map((item) => filterNavigationItem(item))
      .filter(Boolean); // Remove null entries
  });

  // Breadcrumbs computed from current route
  const breadcrumbs = computed(() => {
    const crumbs = [];
    const pathParts = route.path.split('/').filter(Boolean);
  
    let currentPath = '';
    pathParts.forEach((part) => {
      currentPath += '/' + part;
      const navItem = allNavigationItems.find((item) => item.path === currentPath);
      if (navItem) {
        crumbs.push({
          label: navItem.label,
          path: currentPath,
        });
      } else if (currentPath === '/products') {
        // Add Products to breadcrumbs even if not in allNavigationItems yet
        crumbs.push({
          label: 'Products',
          path: currentPath,
        });
      }
    });
  
    return crumbs;
  });

  // Methods
  const toggleDrawer = () => {
    leftDrawerOpen.value = !leftDrawerOpen.value;
  };

  const isActiveRoute = (path) => {
    // Exact match
    if (route.path === path) {return true;}

    // Check if current route starts with this path
    if (!route.path.startsWith(path)) {return false;}

    // Get the remaining part after the path
    const remainder = route.path.slice(path.length);

    // If there's no remainder, it's exact (handled above)
    // If remainder starts with '/' and the next segment is numeric, it's a detail/edit page
    if (remainder.startsWith('/')) {
      const nextSegment = remainder.slice(1).split('/')[0];
      // Match if it's numeric (e.g., /admin/123) or empty
      return /^\d+$/.test(nextSegment) || nextSegment === '';
    }

    return false;
  };

  const createTender = () => {
    router.push('/tenders/new');
  };

  const goToProfile = () => {
    router.push('/profile');
  };

  const goToSettings = () => {
    router.push('/settings');
  };

  const logout = () => {
    authStore.logout();
    // Redirect to Laravel blade login page
    window.location.href = '/login';
  };

  const handleLoading = (loading, message = 'Loading...') => {
    isLoading.value = loading;
    loadingMessage.value = message;
  };

  const handleError = (error) => {
    hasError.value = true;
    errorMessage.value = error.message || 'An unexpected error occurred';
  };

  const retryOperation = () => {
    hasError.value = false;
    errorMessage.value = '';
    // Trigger a refresh of the current route
    router.go(0);
  };

  const goHome = () => {
    hasError.value = false;
    errorMessage.value = '';
    router.push('/dashboard');
  };

  // Auto-save notification
  const showAutoSave = () => {
    showAutoSaveNotification.value = true;
    setTimeout(() => {
      showAutoSaveNotification.value = false;
    }, 2000);
  };

  // Load user settings and apply theme
  const loadUserSettings = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      if (savedTheme === 'dark') {
        document.body.classList.add('body--dark');
        document.body.classList.remove('body--light');
        $q.dark.set(true);
      } else if (savedTheme === 'light') {
        document.body.classList.add('body--light');
        document.body.classList.remove('body--dark');
        $q.dark.set(false);
      } else if (savedTheme === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.body.classList.add('body--dark');
          document.body.classList.remove('body--light');
          $q.dark.set(true);
        } else {
          document.body.classList.add('body--light');
          document.body.classList.remove('body--dark');
          $q.dark.set(false);
        }
      }
    }
  };

  // Global auto-save event listener
  onMounted(async () => {
    window.addEventListener('auto-save-success', showAutoSave);
    loadUserSettings();

    // Check if user data is cached but missing role field - refresh from API
    if (authStore.user && authStore.token && !Object.prototype.hasOwnProperty.call(authStore.user, 'role')) {
      try {
        await authStore.verifyAuth();
      } catch (error) {
        // Auth verification failed - user may need to re-login
        console.warn('[App] Failed to verify authentication on mount:', error.message || error);
        // Clear potentially stale auth state to force re-login
        authStore.clearAuth();
      }
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('auto-save-success', showAutoSave);
  });

  // Watch for route changes to update title
  watch(route, (newRoute) => {
    const currentNav = allNavigationItems.find((item) => 
      newRoute.path.startsWith(item.path),
    );
    if (currentNav) {
      document.title = `${currentNav.label} - The Portal`;
    }
  }, { immediate: true });
</script>

<style scoped>
.layout-root {
  --primary-color: #1976d2;
  --secondary-color: #26a69a;
  --success-color: #21ba45;
  --warning-color: #f2c037;
  --negative-color: #c10015;
  --info-color: #31ccec;
  --dark-color: #1d1d1d;
  --grey-color: #666666;
}

/* Header Improvements */
.layout-header {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  height: 40px;
  width: auto;
}

.app-name {
  font-weight: 600;
  font-size: 1.2rem;
  color: white;
}

.version-chip {
  font-size: 0.75rem;
}

.breadcrumb-section {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.breadcrumbs {
  font-size: 0.9rem;
}

/* Navigation Improvements */
.layout-drawer {
  background: #fafafa;
}

.drawer-layout {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.nav-section {
  flex: 1;
  overflow: hidden;
}

.full-height {
  height: 100%;
}

.navigation-menu {
  padding: 16px 8px;
}

.nav-item {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.nav-item:hover {
  background: rgba(25, 118, 210, 0.1);
}

.nav-item--active {
  background: var(--primary-color);
  color: white;
}

.nav-item--active .q-item__section--avatar .q-icon {
  color: white;
}

/* Expansion menu styles */
.nav-expansion {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.nav-expansion:hover {
  background: rgba(25, 118, 210, 0.05);
}

.nav-item--child {
  padding-left: 20px;
}

.nav-item--child.nav-item--active {
  background: var(--primary-color);
  color: white;
}

.quick-actions {
  flex-shrink: 0;
  padding: 16px;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

/* Content Improvements */
.layout-content {
  background: #f8f9fa;
  padding: 0;
}

/* Remove default padding from all components in improved app */
.layout-root :deep(.q-page) {
  padding: 0;
}

.layout-root :deep(.q-page-container) {
  padding: 0;
}

.layout-root :deep(.q-drawer) {
  padding: 0;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60vh;
}

.loading-content {
  text-align: center;
}

.loading-text {
  margin-top: 16px;
  color: var(--grey-color);
  font-size: 1.1rem;
}

.error-container {
  padding: 24px;
  max-width: 600px;
  margin: 40px auto;
}

/* Auto-save notification */
.auto-save-notification {
  min-width: 250px;
  background: var(--success-color);
  color: white;
}

/* User menu */
.user-menu {
  margin-left: 16px;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .title-section {
    gap: 8px;
  }
  
  .app-name {
    font-size: 1rem;
  }
  
  .version-chip {
    display: none;
  }
  
  .breadcrumb-section {
    padding: 6px 12px;
  }
}
</style>